import { useState } from 'react';
import { useIntl } from 'react-intl';
import { usePullRefreshHandler } from '../../ui/pullRefresh/PullRefreshContext';
import { isMainScrolledForRetap } from '../../utils/ui/activeTabRetapScroll';
import { preventTouchFocusScroll } from '../../utils/ui/mobileListRowInteraction';
import { haptic } from '../../lib/haptics';
import { markNavTabSwitch } from '../../utils/ui/navTabSwitchMark';
import { isForeignDialogOpen } from '../../hooks/ui/useForeignDialogOpen';
import { fg } from '../../ui/theme';
import { foundation } from '../../ui';
import NavGlyph from './navGlyphs/NavGlyph';
import NavNoticeCount from './NavNoticeCount';
import { interceptActiveTabRetap } from '../../utils/ui/activeTabRetapInterceptors';

/** One bottom-bar / dock tab (phone morph chip, tablet dock tab). Pure render of BottomTabBar's tab. */
export default function BottomTabButton({
  tab, activeTab, badgeCounts, compactDock, alwaysLabeled, dockMode, dockTarget, pillBar, dockChipPad,
  dockTransition, reducedMotion, suppressed, hasRefreshHandler, onTabSelect,
}) {
  const intl = useIntl();
  const Icon = tab.icon;
  const label = intl.formatMessage({ id: tab.labelId, defaultMessage: tab.defaultLabel });
  const badgeCount = badgeCounts[tab.key] || 0;
  const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);
  const active = activeTab === tab.key;
  // KAN-991: the phone bar is icons-only (no tab ever shows its name); only
  // the tablet dock is labeled. A label-less tab keeps its count in the icon
  // corner; a labeled one keeps the icon clear.
  const showLabel = alwaysLabeled;
  const countBesideLabel = compactDock && showLabel;
  const color = active ? foundation.color.neutral.text : fg.muted;
  const { refreshPhase } = usePullRefreshHandler();
  const [replayKey, setReplayKey] = useState(0);
  // KAN-994 press feedback (pill bar): icon dips to 0.9 on press, springs back on release.
  const [pressed, setPressed] = useState(false);
  const pressable = pillBar || dockMode === 'dock';
  const pressProps = pressable && !suppressed ? {
    onPointerDown: () => setPressed(true),
    onPointerUp: () => setPressed(false),
    onPointerCancel: () => setPressed(false),
    onPointerLeave: () => setPressed(false),
  } : null;
  // KAN-994 polish: 22px everywhere in the bar + dock (24 read too big on device).
  const iconSize = 22;

  const handleClick = () => {
    if (suppressed) return;
    if (active) {
      if (interceptActiveTabRetap(tab.key)) { haptic('light'); return; } // a screen handled it (KAN-731)
      // Tactile feedback only when the re-tap will DO something: a
      // refresh handler exists, or the page is scrolled down (glide).
      // And never under an open dialog (the shell ignores it there).
      const scrolled = isMainScrolledForRetap();
      const willAct = hasRefreshHandler || scrolled;
      if (scrolled && !isForeignDialogOpen()) setReplayKey((k) => k + 1); // glide to top: icon plays its move
      if (willAct && !isForeignDialogOpen()) haptic('light');
      // Two-stage re-tap (mockup 21): the shell decides — scrolled
      // down → smooth scroll to top; already at top → programmatic
      // refresh with the full pull choreography.
      window.dispatchEvent(new CustomEvent('crewvex-active-tab-retap'));
      return;
    }
    haptic('light');
    markNavTabSwitch(); // nav tap → the shell may restore this tab's scroll (KAN-1019)
    onTabSelect?.(tab.key);
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      // The badge pill is aria-hidden; the COUNT rides in the accessible name.
      aria-label={badgeCount > 0 ? `${label} (${badgeLabel})` : label}
      aria-disabled={suppressed || undefined}
      tabIndex={suppressed ? -1 : undefined}
      onClick={handleClick}
      onMouseDown={suppressed ? undefined : preventTouchFocusScroll}
      data-cvx-press
      {...pressProps}
      {...(pressable && pressed ? { 'data-pressed': 'true' } : {})}
      {...(compactDock ? { 'data-dock-tab-key': tab.key } : {})}
      {...(compactDock && active ? { 'data-active-dock-tab': 'true' } : {})}
      style={compactDock ? {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flex: pillBar ? '1 1 0' : dockMode === 'bar' ? '0 0 auto' : undefined,
        position: 'relative',
        gap: showLabel ? 8 : 0,
        minWidth: pillBar ? 0 : dockTarget,
        height: dockTarget,
        borderRadius: pillBar ? 999 : dockTarget / 2,
        border: 'none',
        backgroundColor: 'transparent',
        zIndex: 1,
        padding: showLabel ? `0 ${dockChipPad}px` : '0',
        cursor: suppressed ? 'default' : 'pointer',
        color,
        transition: reducedMotion
          ? 'none'
          : `padding ${dockTransition}, gap ${dockTransition}, color 180ms ease`,
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
      } : {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        backgroundColor: 'transparent',
        border: 'none',
        padding: '6px 2px 5px',
        cursor: suppressed ? 'default' : 'pointer',
        color,
        transition: 'color 180ms ease, transform 130ms cubic-bezier(0.4, 0, 0.2, 1)',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          width: iconSize,
          height: iconSize,
          flexShrink: 0,
          transform: pressable && pressed ? 'scale(0.9)' : !compactDock && active ? 'scale(1.12)' : 'none',
          transition: reducedMotion
            ? 'none'
            : pressable && pressed
              ? 'transform 90ms ease-out'
              : pressable
                ? 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)'
                : 'transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          willChange: 'transform',
        }}
      >
        <NavGlyph Icon={Icon} size={iconSize} active={active} busy={active && refreshPhase === 'refreshing'} replayKey={replayKey} />
        {!countBesideLabel && (
          <NavNoticeCount
            tone="coral"
            count={badgeCount}
            placement="corner"
            style={{ top: -5, left: '100%', right: 'auto', transform: 'translateX(-6px)' }}
          />
        )}
      </span>
      <span
        style={alwaysLabeled ? {
          // Tablet: the name lives permanently beside its icon.
          fontSize: 12.5,
          fontWeight: active ? 700 : 600,
          letterSpacing: '0.01em',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: 140,
          textOverflow: 'ellipsis',
        } : compactDock ? {
          // Phone bar (KAN-991): icons-only. The name never shows; the node
          // stays (collapsed) so the accessible name and DOM shape hold.
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: 0,
          opacity: 0,
        } : {
          fontSize: 11,
          fontWeight: active ? 600 : 500,
          letterSpacing: '0.01em',
          lineHeight: 1,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      {countBesideLabel && (
        <NavNoticeCount tone="coral" count={badgeCount} morph reducedMotion={reducedMotion} morphGap={8} />
      )}
    </button>
  );
}
